import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import SlackSyncButton from "../components/SlackSyncButton";
import { getApiBaseUrl } from "../utils/apiConfig";
import { getDetailedReportData } from "../utils/slackReportService";
import { slackSyncService } from '../utils/slackSyncService';
import { slackReportService } from '../utils/slackReportService';

const SlackTesting = () => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slackConnected, setSlackConnected] = useState(false);
  const [error, setError] = useState(null);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  
  const navigate = useNavigate();

  // Default date range for the sync button - last 30 days
  const defaultDateRange = {
    startDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date;
    })(),
    endDate: new Date(),
    range: "last30days"
  };

  useEffect(() => {
    // Retrieve user data from localStorage
    const userData = localStorage.getItem("user");
    if (userData && userData !== "undefined") {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        if (parsedUser.organizations && parsedUser.organizations.length > 0) {
          fetchOrganizations(parsedUser.organizations);
          fetchOrganization(parsedUser.organizations[0].organizationId);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (organization) {
      checkSlackConnection();
    }
  }, [organization]);

  const fetchOrganizations = async (orgs) => {
    try {
      const token = localStorage.getItem("token");
      const orgDetails = await Promise.all(
        orgs.map(async (org) => {
          const apiUrl = getApiBaseUrl();
          const response = await axios.get(
            `${apiUrl}/organization/${org.organizationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          return { ...org, name: response.data.name };
        })
      );
      setOrganizations(orgDetails);
    } catch (error) {
      console.error("Error fetching organizations data:", error);
    }
  };

  const fetchOrganization = async (organizationId) => {
    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${apiUrl}/organization/${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setOrganization(response.data);
    } catch (error) {
      console.error("Error fetching organization data:", error);
    }
  };

  const checkSlackConnection = async () => {
    if (!organization) {
      setError("Cannot check Slack connection: organization not loaded");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const orgId = organization._id || organization.id;
      
      if (!orgId) {
        setError("Organization ID is undefined");
        return;
      }
      
      const response = await axios.get(
        `${apiUrl}/integrations/status/${orgId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setSlackConnected(response.data.slack.connected);
    } catch (error) {
      console.error("Error checking Slack connection:", error);
      setError(
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        "Error checking Slack connection"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSyncComplete = async (timestamp) => {
    setSyncCompleted(true);
    
    try {
      setLoading(true);
      const result = await getDetailedReportData(organization);
      
      if (result && result.success && result.data && result.data.reportData) {
        // Log the new reportData structure to console for debugging
        console.log("Report Data Structure:", result.data.reportData);
        
        // Create analysis results from the new structure
        const reportData = result.data.reportData;
        const analysisResults = {
          // Create sentiment analysis from new structure
          sentimentAnalysis: {
            overallScore: reportData.sentiment && reportData.sentiment.sentimentScore ? 
              Object.values(reportData.sentiment.sentimentScore).reduce((sum, score) => sum + score, 0) / 
              Object.values(reportData.sentiment.sentimentScore).length : 0
          },
          // Create engagement analysis from new structure
          engagementAnalysis: {
            overallEngagement: reportData.engagement && reportData.engagement.engagementScore ? 
              Object.values(reportData.engagement.engagementScore).reduce((sum, score) => sum + score, 0) / 
              Object.values(reportData.engagement.engagementScore).length : 0,
            participationRate: reportData.engagement && reportData.engagement.channelEngagement ? 
              Object.keys(reportData.engagement.channelEngagement).length / 10 : 0 // Rough participation rate
          },
          // Create tone analysis from new structure
          toneAnalysis: {
            formal: reportData.tone && reportData.tone.toneScore ? 
              Object.values(reportData.tone.toneScore).reduce((sum, scores) => sum + (scores.formality || 0), 0) / 
              Object.values(reportData.tone.toneScore).length : 0,
            casual: reportData.tone && reportData.tone.toneScore ? 
              Object.values(reportData.tone.toneScore).reduce((sum, scores) => sum + (1 - (scores.formality || 0)), 0) / 
              Object.values(reportData.tone.toneScore).length : 0,
            dominant: reportData.tone && reportData.tone.toneScore ? 
              Object.values(reportData.tone.toneScore).some(scores => (scores.formality || 0) > 0.6) ? 'Formal' : 'Casual' : 'N/A'
          },
          // Create network analysis from channel data
          networkAnalysis: {
            channelActivity: reportData.engagement && reportData.engagement.channelEngagement ? 
              Object.entries(reportData.engagement.channelEngagement).map(([channelName, channelData]) => ({
                channelName,
                messageCount: Object.values(channelData).reduce((sum, dayData) => sum + (dayData.messageCount || 0), 0)
              })) : []
          }
        };
        
        console.log("Processed Analysis Results:", analysisResults);
        setAnalysisResults(analysisResults);
      } else {
        console.log("No valid report data found");
        setAnalysisResults(null);
      }
    } catch (error) {
      console.error("Error fetching analysis results:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDebugData = async () => {
    try {
      setLoading(true);
      const organizationId = localStorage.getItem('organizationId');
      if (!organizationId) {
        console.error('No organization ID found');
        return;
      }
      
      console.log('Fetching debug data...');
      const response = await fetch(`/api/slack-reports/debug/${organizationId}?rangeType=last30days`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      console.log('Debug data:', data);
      setDebugData(data.data);
      setShowDebug(true);
    } catch (error) {
      console.error('Error fetching debug data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRawReportData = async () => {
    try {
      setLoading(true);
      const organizationId = localStorage.getItem('organizationId');
      if (!organizationId) {
        console.error('No organization ID found');
        return;
      }
      
      console.log('Fetching raw report data...');
      const response = await fetch(`/api/slack-reports/organization/${organizationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      console.log('Raw report data:', data);
      console.log('Report data structure:', data.data?.reportData);
      if (data.data?.reportData?.engagement) {
        console.log('Engagement keys:', Object.keys(data.data.reportData.engagement));
        console.log('Engagement score sample:', Object.entries(data.data.reportData.engagement.engagementScore || {}).slice(0, 3));
        console.log('Activity sample:', Object.entries(data.data.reportData.engagement.activity || {}).slice(0, 3));
        console.log('Channel engagement sample:', Object.entries(data.data.reportData.engagement.channelEngagement || {}).slice(0, 3));
      }
      setDebugData(data.data);
      setShowDebug(true);
    } catch (error) {
      console.error('Error fetching raw report data:', error);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="h-screen h-dvh overflow-hidden flex flex-row bg-bg-gradient-dash">
      <Sidebar 
        user={user} 
        organization={organization} 
        organizations={organizations} 
      />
      <div className="flex-1 overflow-y-auto flex flex-col p-6 gap-4">
        <h1 className="text-2xl font-normal text-black-0 mb-2">Slack Data Analysis</h1>
        <p className="text-sm text-grey-60 mb-6">
          Analyze your Slack workspace data for engagement, tone, clarity, sentiment, and topics.
        </p>
        
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin"></div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-light p-4 rounded-md text-red-strong mb-4">
            {error}
          </div>
        )}
        
        {!slackConnected && !loading && (
          <div className="bg-grey-20 p-4 rounded-md mb-4">
            <p className="text-grey-60">
              You need to connect to Slack first. Please go to the{" "}
              <button 
                onClick={() => navigate('/settings')}
                className="text-brand hover:underline"
              >
                Settings page
              </button>{" "}
              to set up your Slack connection.
            </p>
          </div>
        )}
        
        {slackConnected && !loading && (
          <>
            <div className="bg-white-f p-6 rounded-md shadow-subtle mb-4">
              <h2 className="text-lg font-medium text-black-0 mb-4">Analyze Slack Data</h2>
              <p className="text-sm text-grey-60 mb-4">
                Click the button below to sync and analyze data from your Slack workspace. This will:
              </p>
              
              <ul className="list-disc pl-5 mb-4 text-sm text-grey-60">
                <li>Fetch messages from the last 3 months (default)</li>
                <li>Analyze engagement, tone, clarity, sentiment, and topics</li>
                <li>Store daily analysis results for future reference</li>
              </ul>
              
              <div className="max-w-xs">
                <SlackSyncButton 
                  organization={organization} 
                  onSyncComplete={handleSyncComplete}
                />
              </div>
            </div>
            
            {/* Analysis Results Summary */}
            {syncCompleted && analysisResults && (
              <div className="bg-white-f p-6 rounded-md shadow-subtle mb-4">
                <h2 className="text-lg font-medium text-black-0 mb-4">Analysis Summary</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sentiment Analysis */}
                  <div className="border border-grey-20 rounded-md p-4">
                    <h3 className="font-medium mb-2">Sentiment Analysis</h3>
                    <p className="text-sm text-grey-80">
                      Overall Score: {analysisResults.sentimentAnalysis?.overallScore?.toFixed(2) || 'N/A'}
                    </p>
                          </div>
                  
                  {/* Engagement */}
                  <div className="border border-grey-20 rounded-md p-4">
                    <h3 className="font-medium mb-2">Engagement</h3>
                    <p className="text-sm text-grey-80">
                      Overall Engagement: {analysisResults.engagementAnalysis?.overallEngagement?.toFixed(2) || 'N/A'}
                            </p>
                    <p className="text-sm text-grey-80">
                      Participation Rate: {analysisResults.engagementAnalysis?.participationRate?.toFixed(2) || 'N/A'}
                            </p>
                          </div>
                  
                  {/* Communication Tone */}
                  <div className="border border-grey-20 rounded-md p-4">
                    <h3 className="font-medium mb-2">Communication Tone</h3>
                    <p className="text-sm text-grey-80">
                      Formal: {analysisResults.toneAnalysis?.formal?.toFixed(2) || 'N/A'}
                    </p>
                    <p className="text-sm text-grey-80">
                      Casual: {analysisResults.toneAnalysis?.casual?.toFixed(2) || 'N/A'}
                    </p>
                    <p className="text-sm text-grey-80">
                      Dominant Style: {analysisResults.toneAnalysis?.dominant || 'N/A'}
                    </p>
                        </div>
                  
                  {/* Channel Activity */}
                  <div className="border border-grey-20 rounded-md p-4">
                    <h3 className="font-medium mb-2">Channel Activity</h3>
                    {analysisResults.networkAnalysis?.channelActivity?.length > 0 ? (
                      <p className="text-sm text-grey-80">
                        Most active: {analysisResults.networkAnalysis.channelActivity
                          .sort((a, b) => b.messageCount - a.messageCount)
                          .slice(0, 3)
                          .map(c => c.channelName)
                          .join(', ')}
                      </p>
                    ) : (
                      <p className="text-sm text-grey-60">No channel activity data available</p>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-grey-60 mt-4">
                  This is a summary of the analysis. Detailed daily metrics have been stored in the database.
                </p>
              </div>
            )}
          </>
        )}
        
        <div className="mb-6">
          <button 
            onClick={fetchDebugData} 
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded mr-4"
            disabled={loading}
          >
            Debug Report Data
          </button>
          <button 
            onClick={fetchRawReportData} 
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
            disabled={loading}
          >
            Fetch Raw Report Data
          </button>
        </div>
        
        {showDebug && debugData && (
          <div className="mt-8 p-4 border border-gray-300 rounded">
            <h2 className="text-xl font-bold mb-4">Debug Information</h2>
            
            <div className="mb-4">
              <h3 className="text-lg font-semibold">Report Metadata</h3>
              <p>Report ID: {debugData.reportId}</p>
              <p>Updated At: {new Date(debugData.updatedAt).toLocaleString()}</p>
              <p>Date Range: {debugData.dateRange?.startDate} to {debugData.dateRange?.endDate}</p>
              <p>Has Raw Data: {debugData.hasRawData ? 'Yes' : 'No'}</p>
              <p>Message Count: {debugData.messageCount}</p>
              <p>Daily Analyses Count: {debugData.dailyAnalysesCount}</p>
            </div>
            
            {debugData.messageSample && debugData.messageSample.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Message Sample</h3>
                <pre className="bg-gray-100 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(debugData.messageSample, null, 2)}
                </pre>
              </div>
            )}
            
            {debugData.fullAnalysis && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Full Analysis</h3>
                <pre className="bg-gray-100 p-2 rounded overflow-auto max-h-96">
                  {JSON.stringify(debugData.fullAnalysis, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SlackTesting; 